import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { env } from '@config/env';
import { tokenStorage } from '@features/auth/utils/tokenStorage';
import { ApiError, NetworkError, ValidationError } from '@core/network/types/apiError';
import type { FieldErrors } from '@core/types/api.types';
import { logger } from '@core/utils/logger';

/** Broadcast when the API rejects our credentials so the auth store can log out. */
export const UNAUTHORIZED_EVENT = 'dda:unauthorized';

interface ApiErrorBody {
  readonly message?: string;
  readonly code?: string;
  /** Backend's non-validation error envelope: { error: { code, message } }. */
  readonly error?: { readonly code?: string; readonly message?: string };
  /** FastAPI validation payload. */
  readonly detail?: string | { readonly loc?: (string | number)[]; readonly msg?: string }[];
  readonly fieldErrors?: FieldErrors;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function normalizeError(error: AxiosError<ApiErrorBody>): never {
  // No response → offline / timeout / DNS.
  if (!error.response) {
    throw new NetworkError(error.message || 'Network request failed');
  }

  const { status, data } = error.response;

  // Map FastAPI-style validation payloads to field errors for forms.
  let message = data?.message ?? data?.error?.message ?? '';
  const code = data?.code ?? data?.error?.code;
  const fieldErrors: FieldErrors = { ...(data?.fieldErrors ?? {}) };
  if (Array.isArray(data?.detail)) {
    for (const d of data.detail) {
      const loc = d.loc?.[d.loc.length - 1];
      const field = loc != null ? String(loc) : undefined;
      if (field && d.msg) fieldErrors[field] = d.msg;
    }
    if (!message) message = 'Validation failed';
  } else if (typeof data?.detail === 'string') {
    message = message || data.detail;
  }
  if (!message) message = `Request failed (${status})`;

  if (status === 401) {
    tokenStorage.clear();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }

  if (status === 422) {
    throw new ValidationError(message, fieldErrors);
  }

  logger.warn('API error', { status, code });
  throw new ApiError(status, code ?? 'api_error', message, fieldErrors);
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiErrorBody>) => normalizeError(error),
);
